/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { authService, projectService } from '../services/api';

/**
 * Hook for managing the globally selected project.
 * The selected project is persisted to the backend and restored on login.
 */
export const useSelectedProject = () => {
  const queryClient = useQueryClient();
  const { user, updateUser, isAuthenticated, isLoading: authLoading } = useAuthStore();

  // Fetch projects list
  const { data: projectsData, isLoading, isPending: projectsPending } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.list().then((res) => res.data),
    enabled: isAuthenticated && !authLoading,
  });

  const projectsLoading = isLoading || projectsPending;

  const projects = useMemo(
    () =>
      projectsData?.projects ||
      (Array.isArray(projectsData) ? projectsData : []),
    [projectsData]
  );

  // Get the current selected project ID from user profile
  const selectedProjectId = user?.selected_project_id || '';

  // Find the selected project object
  const selectedProject = projects.find((p: any) => p.id === selectedProjectId) || null;

  // Mutation to update selected project in backend
  const updateProjectMutation = useMutation({
    mutationFn: (projectId: string) =>
      authService.updateProfile({ selected_project_id: projectId }),
    onSuccess: () => {
      // Invalidate queries that depend on the selected project
      queryClient.invalidateQueries({ queryKey: ['notebooks'] });
      queryClient.invalidateQueries({ queryKey: ['containers'] });
      queryClient.invalidateQueries({ queryKey: ['volumes'] });
      queryClient.invalidateQueries({ queryKey: ['buckets'] });
      queryClient.invalidateQueries({ queryKey: ['vms'] });
    },
    onError: () => {
      // Silently fail - local state is already updated optimistically
      // The selection will be re-synced on next login when backend is fixed
    },
    retry: false, // Don't retry failed requests
  });

  // Function to change the selected project (optimistic update)
  const setSelectedProject = useCallback(
    (projectId: string) => {
      if (projectId !== selectedProjectId) {
        // Update local state immediately (optimistic)
        updateUser({ selected_project_id: projectId });
        // Then sync to backend (may fail silently)
        updateProjectMutation.mutate(projectId);
      }
    },
    [selectedProjectId, updateProjectMutation, updateUser]
  );

  // Auto-select first project if none selected and projects are loaded
  const autoSelectFirstProject = useCallback(() => {
    if (!selectedProjectId && projects.length > 0 && !updateProjectMutation.isPending) {
      setSelectedProject(projects[0].id);
    }
  }, [selectedProjectId, projects, updateProjectMutation.isPending, setSelectedProject]);

  return {
    // Current selection
    selectedProjectId,
    selectedProject,

    // Available projects
    projects,
    projectsLoading,

    // Actions
    setSelectedProject,
    autoSelectFirstProject,

    // Loading state
    isUpdating: updateProjectMutation.isPending,
  };
};

export default useSelectedProject;
